import { useState } from "react";
import { Gift, ArrowRight, CheckCircle, DownloadSimple } from "@phosphor-icons/react";
import { track, identify } from "@/lib/posthogClient";
import { identify as swsIdentify, trackConversion } from "@/lib/analyticsClient";

// FormSubmit forwards the email lead straight to sweatwithslay@gmail.com.
// First submission triggers a one-time activation email — AJ must click the
// confirmation link in that email once, then all future submissions flow through.
const FORM_ENDPOINT = "https://formsubmit.co/ajax/sweatwithslay@gmail.com";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const guides = [
  {
    title: "The 20-min hotel-room workout",
    desc: "No gym, no equipment — just five moves that travel.",
    url: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/asjucynq_SWS_LeadMagnet_Hotel_Workout.pdf",
    file: "SWS_Hotel_Workout.pdf",
  },
  {
    title: "14 honest days",
    desc: "How to come back to training without burning out by Friday.",
    url: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/566wuetl_SWS_LeadMagnet_14_Honest_Days.pdf",
    file: "SWS_14_Honest_Days.pdf",
  },
  {
    title: "Busy-week eating",
    desc: "Protein hits, repeat meals, and the snack I keep in every bag.",
    url: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/115i8bd5_SWS_LeadMagnet_Busy_Week_Eating.pdf",
    file: "SWS_Busy_Week_Eating.pdf",
  },
];

export default function LeadMagnet() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    // Fire backend lead capture (IP + geo enrichment) in parallel — non-blocking
    fetch(`${BACKEND}/api/leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        name: name || "",
        source: "Pink Plan starter pack",
        referrer: typeof document !== "undefined" ? document.referrer : "",
        page: typeof window !== "undefined" ? window.location.pathname : "/",
      }),
    }).catch(() => { /* silent — FormSubmit is still primary */ });

    // PostHog: identify + capture
    try {
      identify(email, { email, name: name || "" });
      track("lead_magnet_submitted", { name: name || "", email });
    } catch { /* no-op */ }

    // Sweat With Slay analytics — link session and record conversion
    try {
      swsIdentify(email, "lead", name || "");
      trackConversion("lead_form_submit", { email });
    } catch { /* no-op */ }

    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          email,
          name: name || "(not provided)",
          _subject: "New Pink Plan lead — Sweat With Slay",
          _template: "table",
          source: "sweatwithslay.com / Pink Plan starter guide",
        }),
      });
      if (res.ok) {
        setStatus("done");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="free-guide"
      data-testid="leadmagnet-section"
      className="relative px-6 md:px-12 lg:px-20 py-20 border-b border-white/10 bg-gradient-to-br from-[#1a0712] via-[#0A0A0A] to-[#0A0A0A] overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-[30rem] h-[30rem] rounded-full bg-[#FF1B91]/10 blur-[100px] pointer-events-none" />
      <div className="relative max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FF1B91] font-semibold bg-[#FF1B91]/10 border border-[#FF1B91]/30 px-3 py-1.5 mb-6">
            <Gift size={14} weight="fill" /> Free 3-pack download
          </div>
          <h2 className="font-display font-black uppercase text-3xl md:text-4xl lg:text-5xl leading-[0.95] tracking-tighter mb-5">
            The Pink Plan{" "}
            <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">starter pack.</span>
          </h2>
          <p className="text-white/75 leading-relaxed max-w-md mb-6">
            Three PDFs to plug straight into your week:
          </p>
          <ul className="space-y-2.5 text-white/80 text-sm">
            <li className="flex gap-2.5"><span className="text-[#FF1B91] font-bold">✦</span> The 20-min hotel-room workout</li>
            <li className="flex gap-2.5"><span className="text-[#FF1B91] font-bold">✦</span> 14 honest days — return-to-training</li>
            <li className="flex gap-2.5"><span className="text-[#FF1B91] font-bold">✦</span> Busy-week eating &amp; protein hits</li>
          </ul>
        </div>

        <div className="border border-white/10 bg-[#0F0F0F]/80 backdrop-blur-md p-8">
          {status === "done" ? (
            <div data-testid="leadmagnet-success" className="space-y-5">
              <div className="text-center pb-2">
                <CheckCircle size={44} weight="fill" className="text-[#FF1B91] mx-auto mb-3" />
                <h3 className="font-body font-semibold text-xl mb-1">You&apos;re in.</h3>
                <p className="text-sm text-white/65">Grab all three below — they&apos;re yours.</p>
              </div>
              <div className="space-y-3">
                {guides.map((g, i) => (
                  <a
                    key={g.file}
                    href={g.url}
                    download={g.file}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`leadmagnet-download-${i}`}
                    className="group flex items-start gap-3 border border-white/10 hover:border-[#FF1B91] hover:bg-[#FF1B91]/5 transition-all p-4"
                  >
                    <div className="w-10 h-10 shrink-0 border border-[#FF1B91] flex items-center justify-center text-[#FF1B91] group-hover:bg-[#FF1B91] group-hover:text-white transition-colors">
                      <DownloadSimple size={18} weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-body font-semibold text-[15px] leading-tight">{g.title}</div>
                      <div className="text-xs text-white/55 mt-1 leading-snug">{g.desc}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-semibold">First name (optional)</span>
                <input
                  data-testid="leadmagnet-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-brutal mt-2"
                  placeholder="Slay"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-semibold">Your best email</span>
                <input
                  data-testid="leadmagnet-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-brutal mt-2"
                  placeholder="you@email.com"
                />
              </label>
              <button
                data-testid="leadmagnet-submit"
                type="submit"
                disabled={status === "sending"}
                className="w-full inline-flex items-center justify-center gap-3 bg-[#FF1B91] hover:bg-[#FF3FA3] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] btn-brutal btn-brutal-pink"
              >
                {status === "sending" ? "Sending…" : <>Send me all 3 PDFs <ArrowRight size={14} weight="bold" /></>}
              </button>
              {status === "error" && (
                <p data-testid="leadmagnet-error" className="text-xs text-[#FF1B91] text-center">
                  Hmm — that didn&apos;t send. Email <a href="mailto:sweatwithslay@gmail.com" className="underline">sweatwithslay@gmail.com</a> and I&apos;ll get them to you.
                </p>
              )}
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/35 text-center pt-1">
                One email. No list, no spam.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
